import React, { useMemo } from 'react';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { AppDispatch, RootStateType } from '@Store';
import SBDropdown from '@Components/Dropdown';
import SbButton from '@Components/Button';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';
import { setIsWarehouseSelectedINFedX } from '@Reducers/Warehouse';
import { vendorAccountType } from '@Reducers/AdminShipment/Type';
import {
  ApiServiceCodeOptions,
  onForward,
  ProductCodeOptions,
  TermsOfTradeOptions,
  universalInformationSchema,
} from './constants';

interface Props {
  onClickForward: () => void;
  onClose?: () => void;
  initialStates: any;
  isEditable?: boolean;
  showFedExDetails: boolean;
  btnText: string;
}

const UniversalCourierInformation: React.FC<Props> = ({
  onClickForward,
  onClose,
  initialStates,
  isEditable = false,
  showFedExDetails,
  btnText,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isWarehouseSelectedINFedX } = useSelector((state: RootStateType) => state.Warehouse);
  const { shipmentById, vendersAccount, universalDetails } = useSelector((state: RootStateType) => state.adminShipment);
  const universalSettings = useSelector((state: RootStateType) => state.user.carrierSetting.universal);

  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })) ?? [],
    [vendersAccount],
  );

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      accountNo: '',
      apiServiceCode: '',
      productCode: '',
      termsOfTrade: '',
      type: '',
      freeFormCurrency: '',
      newDocketFreeFormInvoice: '',
      ...initialStates,
    },
    validationSchema: universalInformationSchema,
    onSubmit: values => {
      if (!isWarehouseSelectedINFedX || isWarehouseSelectedINFedX === 'false') {
        dispatch(setIsWarehouseSelectedINFedX('false'));
      }
      onForward({
        values,
        shipmentNumber: shipmentById?.shipmentNumber || '',
        dispatch,
        nextFunc: onClickForward,
      });
    },
  });

  const { values, setFieldValue, errors, handleSubmit } = formik;

  if (!showFedExDetails) return null;

  return (
    <Paper variant="outlined" sx={{ my: '2rem', p: '2rem' }}>
      <Typography className="font-medium l-text pb-3" mb={3}>
        Universal Information
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="accountNo"
            label="Universal Account"
            required
            placeholder="Select"
            name="accountNo"
            value={[
              universalSettings?.accountNo?.length
                ? universalSettings?.accountNo?.find(() => values?.accountNo)?.value || ''
                : accountOptions?.find(item => values?.accountNo && item.value?.includes(values?.accountNo))?.value ||
                  '',
            ]}
            options={universalSettings?.accountNo?.length ? universalSettings?.accountNo : accountOptions}
            onChange={val => setFieldValue('accountNo', val[0] || '')}
            error={errors.accountNo as string}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="apiServiceCode"
            label="Service Code"
            required
            placeholder="Select"
            name="apiServiceCode"
            value={values.apiServiceCode ? [values.apiServiceCode] : []}
            options={ApiServiceCodeOptions}
            onChange={val => setFieldValue('apiServiceCode', val[0] || '')}
            error={errors.apiServiceCode as string}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="productCode"
            label="Product Code"
            required
            placeholder="Select"
            name="productCode"
            value={values.productCode ? [values.productCode] : []}
            options={ProductCodeOptions}
            onChange={val => {
              const selected = val[0] || '';
              setFieldValue('productCode', selected);

              // ✅ Set other fields conditionally
              if (selected === 'NONDOX') {
                setFieldValue('freeFormCurrency', 1);
                setFieldValue('newDocketFreeFormInvoice', 1);
              } else {
                setFieldValue('freeFormCurrency', 0);
                setFieldValue('newDocketFreeFormInvoice', 0);
              }
            }}
            error={errors.productCode as string}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="termsOfTrade"
            label="Terms of Trade"
            required
            placeholder="Select"
            name="termsOfTrade"
            value={values.termsOfTrade ? [values.termsOfTrade] : []}
            options={TermsOfTradeOptions}
            onChange={val => setFieldValue('termsOfTrade', val[0] || '')}
            error={errors.termsOfTrade as string}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <SbButton
            disabled={!isEditable}
            variant="contained"
            sx={{ borderRadius: 1 }}
            fullWidth
            onClick={() => handleSubmit()}
          >
            {btnText}
          </SbButton>
        </Grid>

        {onClose && (
          <Grid item xs={12} sm={6} md={3}>
            <SbButton variant="outlined" sx={{ borderRadius: 1 }} fullWidth onClick={onClose}>
              Cancel
            </SbButton>
          </Grid>
        )}
      </Grid>

      {universalDetails?.response && (
        <Grid container mt={3}>
          <Grid item xs="auto" border="1px solid" className="border-grey font-medium md-text p-2" borderRadius={1}>
            This shipment has been forwarded.
            <Box
              component="span"
              sx={{
                color: '#485fc7',
                textDecoration: 'underline',
                cursor: 'pointer',
                ml: 1,
              }}
              onClick={() => {
                if (shipmentById?.shipmentNumber) {
                  dispatch(downloadCarrierLabel(shipmentById.shipmentNumber));
                }
              }}
            >
              Download the carrier AWB
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default UniversalCourierInformation;
